import { useCallback } from 'react';
import { useRegisterUser } from './useRegisterUser';
import { useSchema } from './useSchema';
import { useDynamicFieldsStorage } from './useDynamicFieldsStorage';
import { ApiLocalStorage } from 'utils/ApiLocalStorage';
import { SCHEMA_STORAGE_KEY } from '../services/SchemaService';
import { createUserData } from '../constantes/UserDataCreationService';
import { notification } from 'components/ds';

interface SchemaItem {
  id: string;
  fileId: string;
}

export const useRegisterUserLogic = () => {
  const {registerUser, isRegistering, registerError, hasRegisterError} = useRegisterUser();
  const {getSchemaId} = useSchema();
  const {registrationData} = useDynamicFieldsStorage();
  
  /**
   * Valida se todos os dados necessários para registro estão disponíveis
   */
  const validateRegistrationData = useCallback((): {
    schemaId: string;
    username: string;
    schemaData: any;
  } => {
    const schemaId = getSchemaId();
    const username = ApiLocalStorage.get('@pwa-username');
    const schemaData = ApiLocalStorage.get(SCHEMA_STORAGE_KEY);
    
    if (!schemaId) {
      throw new Error('Schema ID não encontrado');
    }
    
    if (!username) {
      throw new Error('Username não encontrado no localStorage');
    }
    
    if (!schemaData) {
      throw new Error('Dados do schema não encontrados no localStorage');
    }
    
    return {schemaId, username, schemaData};
    
  }, [getSchemaId]);
  
  /**
   * Prepara os dados de termos baseado no schema
   */
  const prepareTermsData = useCallback((schemaData: any): Array<{ termId: string; fileId: string }> => schemaData.terms?.map((item: SchemaItem) => ({
      termId: item.id,
      fileId: item.fileId
    })) || [], []);
  
  /**
   * Mapeia o canal OTP para o formato esperado pela API
   */
  const mapOtpChannelToApiFormat = useCallback((channel?: string): string | undefined => {
    if (!channel) return undefined;
    
    const channelMap: Record<string, string> = {
      'email': 'Email',
      'sms': 'SMS',
      'whatsApp': 'WhatsApp'
    };
    
    return channelMap[channel.toLowerCase()] || channel;
  }, []);

  /**
   * Manipula o processo de registro do usuário
   * @param options Opções de registro
   * @param options.passwordData Dados de senha opcionais
   * @param options.captchaToken Token do captcha opcional
   * @param options.channel Canal OTP selecionado (opcional)
   * @returns Promise<string> participantId do usuário registrado
   */
  const handleRegisterUser = useCallback(async (options?: {
    passwordData?: {
      password: string;
      confirmPassword: string;
    };
    captchaToken?: string;
    channel?: string;
  }): Promise<string> => {
    const { passwordData, captchaToken, channel } = options || {};
    try {
      // Validar dados necessários
      const {schemaId, username, schemaData} = validateRegistrationData();
      
      // Preparar dados dos termos
      const termsData = prepareTermsData(schemaData);
      
      // Criar dados do usuário
      const registerData = createUserData(schemaId, registrationData, username, termsData, passwordData);
      
      // Adicionar captchaToken e 2faValidation se fornecido
      const registerDataWithExtras: any = { ...registerData };
      
      if (captchaToken) {
        registerDataWithExtras.captchaToken = captchaToken;
      }
      
      // Adicionar 2faValidation se o canal estiver disponível
      const mappedChannel = mapOtpChannelToApiFormat(channel);
      if (mappedChannel) {
        registerDataWithExtras['2faValidation'] = {
          channel: mappedChannel
        };
      }
      
      // Adicionar credentials se passwordData estiver disponível
      // Remove o campo password antigo se existir, pois agora usamos credentials
      if (passwordData) {
        delete registerDataWithExtras.password;
        registerDataWithExtras.credentials = {
          password: passwordData.password,
          confirmPassword: passwordData.confirmPassword
        };
      }
      
      // Executar registro
      return new Promise((resolve, reject) => {
        registerUser(registerDataWithExtras, {
          onSuccess: (response: any) => {
            console.log('Registro realizado com sucesso:', response);
            
            if (response?.participantId) {
              // Salvar participantId no localStorage
              ApiLocalStorage.set('@pwa-participantId', response.participantId);
              
              notification.success({
                message: 'Registro realizado',
                description: 'Usuário registrado com sucesso!'
              });
              
              resolve(response.participantId);
            } else {
              const error = new Error('ParticipantId não retornado no registro');
              console.error(error);
              reject(error);
            }
          },
          onError: (error: any) => {
            console.error('Erro no registro:', error);
            
            notification.error({
              message: 'Erro no registro',
              description: 'Não foi possível registrar o usuário. Tente novamente.'
            });
            
            reject(error);
          }
        });
      });
      
    } catch (error) {
      console.error('Erro ao preparar dados para registro:', error);
      
      notification.error({
        message: 'Erro ao enviar código de validaçãoo',
        description: 'Não foi possível enviar o código de verificação. Por favor, verifique seus dados e tente novamente.'
      });
      
      throw error;
    }
  }, [
    validateRegistrationData,
    prepareTermsData,
    registrationData,
    registerUser,
    mapOtpChannelToApiFormat
  ]);
  
  /**
   * Verifica se o registro pode ser executado
   */
  const canRegister = useCallback((): boolean => {
    try {
      validateRegistrationData();
      
      return !!registrationData;
    } catch {
      return false;
    }
  }, [validateRegistrationData, registrationData]);
  
  return {
    // Métodos principais
    handleRegisterUser,
    
    // Métodos auxiliares
    validateRegistrationData,
    prepareTermsData,
    canRegister,
    
    // Estados
    isRegistering,
    registerError,
    hasRegisterError
  };
};
